/**
 * Settings Process
 * 
 * Production-ready settings interface with comprehensive tabs for all user preferences.
 * Similar to ChatGPT, Claude, or Manus settings.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { state } from '../../core/state.js';
import { log } from '../../services/logging/index.js';
import { 
  designTokens, 
  createHeading, 
  createContainer,
  createTextInput,
  createSelect,
  createButton,
  createFormField,
  escapeHtml as escapeHtmlUtil
} from '../../core/design-system.js';

// Settings data structure
export interface SettingsData {
  // Profile
  profile: {
    displayName: string;
    email: string;
    avatar?: string;
    bio?: string;
  };
  
  // Preferences
  preferences: {
    theme: 'light' | 'dark' | 'auto';
    backgroundTheme: 'dark' | 'day'; // New setting
    language: string;
    timezone: string;
    dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
    timeFormat: '12h' | '24h';
    firstDayOfWeek: 'sunday' | 'monday';
  };
  
  // Notifications
  notifications: {
    emailEnabled: boolean;
    pushEnabled: boolean;
    soundEnabled: boolean;
    proactiveFrequency: 'never' | 'low' | 'normal' | 'high';
    emailDigest: 'never' | 'daily' | 'weekly';
    desktopNotifications: boolean;
  };
  
  // Privacy & Data
  privacy: {
    dataSharing: boolean;
    analyticsOptOut: boolean;
    crashReporting: boolean;
    usageData: boolean;
    exportData: boolean;
    deleteAccount: boolean;
  };
  
  // Voice & Audio
  voice: {
    microphoneEnabled: boolean;
    wakeWord: string;
    ttsEnabled: boolean;
    ttsVoice: string;
    ttsSpeed: number; // 0.5 - 2.0
    audioOutput: string;
  };
  
  // AI Behavior
  ai: {
    responseStyle: 'concise' | 'balanced' | 'detailed';
    proactivity: 'passive' | 'moderate' | 'proactive';
    contextLength: 'short' | 'medium' | 'long';
    temperature: number; // 0.0 - 2.0
    systemPersonality: string;
  };
  
  // Storage & Sync
  storage: {
    cloudSyncEnabled: boolean;
    autoBackup: boolean;
    backupFrequency: 'daily' | 'weekly' | 'monthly';
    localStorageLimit: number; // MB
    cloudStorageUsed: number; // MB
  };
  
  // Billing
  billing: {
    plan: 'free' | 'pro' | 'enterprise';
    subscriptionStatus: 'active' | 'cancelled' | 'trial';
    nextBillingDate?: string;
    paymentMethod?: string;
    usageThisMonth: {
      requests: number;
      storage: number; // MB
    };
  };
  
  // Security
  security: {
    twoFactorEnabled: boolean;
    sessionTimeout: number; // minutes
    requirePasswordForSensitive: boolean;
    loginAlerts: boolean;
    activeSessions: number;
  };
  
  // Accessibility
  accessibility: {
    screenReader: boolean;
    fontSize: 'small' | 'medium' | 'large' | 'xlarge';
    highContrast: boolean;
    reducedMotion: boolean;
    keyboardShortcuts: boolean;
  };
  
  // Advanced
  advanced: {
    developerMode: boolean;
    apiKey?: string;
    debugMode: boolean;
    logLevel: 'error' | 'warn' | 'info' | 'debug';
    experimentalFeatures: boolean;
  };
  
  // About
  about: {
    version: string;
    buildDate: string;
    license: string;
  };
  
  // Ambient
  ambient: {
    enabled: boolean;
    suite: 'final' | 'dawn' | 'day' | 'dream' | 'womb';
  };

  // Captions
  captions: {
    enabled: boolean;
    size: 'small' | 'medium' | 'large' | 'xlarge';
  };
}

type SettingsTab = 
  | 'profile'
  | 'preferences'
  | 'notifications'
  | 'privacy'
  | 'voice'
  | 'captions'
  | 'audio'
  | 'ambient'
  | 'ai'
  | 'storage'
  | 'billing'
  | 'security'
  | 'accessibility'
  | 'advanced'
  | 'about';

export class SettingsProcess extends BaseProcess {
  kind: string = 'settings';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private settings: SettingsData;
  private settingsView: SettingsView | null = null;

  constructor(id: string) {
    super(id);
    this.settings = this.getDefaultSettings();
    this.loadSettings();
  }

  private getDefaultSettings(): SettingsData {
    return {
      profile: {
        displayName: 'User',
        email: 'user@example.com',
        bio: ''
      },
      preferences: {
        theme: 'dark',
        backgroundTheme: 'day', // Default to day mode
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dateFormat: 'MM/DD/YYYY',
        timeFormat: '12h',
        firstDayOfWeek: 'sunday'
      },
      notifications: {
        emailEnabled: true,
        pushEnabled: false,
        soundEnabled: true,
        proactiveFrequency: 'normal',
        emailDigest: 'weekly',
        desktopNotifications: true
      },
      privacy: {
        dataSharing: false,
        analyticsOptOut: false,
        crashReporting: true,
        usageData: true,
        exportData: true,
        deleteAccount: false
      },
      voice: {
        microphoneEnabled: true,
        wakeWord: 'Hey Pivot',
        ttsEnabled: false,
        ttsVoice: 'default',
        ttsSpeed: 1.0,
        audioOutput: 'default'
      },
      ai: {
        responseStyle: 'balanced',
        proactivity: 'moderate',
        contextLength: 'medium',
        temperature: 0.7,
        systemPersonality: 'helpful, concise, professional'
      },
      storage: {
        cloudSyncEnabled: true,
        autoBackup: true,
        backupFrequency: 'daily',
        localStorageLimit: 100,
        cloudStorageUsed: 0
      },
      billing: {
        plan: 'free',
        subscriptionStatus: 'active',
        usageThisMonth: {
          requests: 0,
          storage: 0
        }
      },
      security: {
        twoFactorEnabled: false,
        sessionTimeout: 60,
        requirePasswordForSensitive: true,
        loginAlerts: true,
        activeSessions: 1
      },
      accessibility: {
        screenReader: false,
        fontSize: 'medium',
        highContrast: false,
        reducedMotion: false,
        keyboardShortcuts: true
      },
      advanced: {
        developerMode: false,
        debugMode: false,
        logLevel: 'info',
        experimentalFeatures: false
      },
      about: {
        version: '1.0.0',
        buildDate: new Date().toISOString().split('T')[0],
        license: 'Proprietary'
      },
      ambient: {
        enabled: true,
        suite: 'final'
      },
      captions: {
        enabled: true,
        size: 'medium'
      }
    };
  }

  private loadSettings(): void {
    // TODO: Load from API/localStorage
    // For now, use defaults
    try {
      const saved = localStorage.getItem('pivot-settings');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<SettingsData>;
        // Deep merge to preserve nested structure
        this.settings = {
          ...this.settings,
          ...(parsed as Record<string, unknown>),
          profile: { ...this.settings.profile, ...(parsed.profile as Record<string, unknown> || {}) },
          preferences: { ...this.settings.preferences, ...(parsed.preferences as Record<string, unknown> || {}) },
          notifications: { ...this.settings.notifications, ...(parsed.notifications as Record<string, unknown> || {}) },
          privacy: { ...this.settings.privacy, ...(parsed.privacy as Record<string, unknown> || {}) },
          voice: { ...this.settings.voice, ...(parsed.voice as Record<string, unknown> || {}) },
          ai: { ...this.settings.ai, ...(parsed.ai as Record<string, unknown> || {}) },
          storage: { ...this.settings.storage, ...(parsed.storage as Record<string, unknown> || {}) },
          billing: { ...this.settings.billing, ...(parsed.billing as Record<string, unknown> || {}) },
          security: { ...this.settings.security, ...(parsed.security as Record<string, unknown> || {}) },
          accessibility: { ...this.settings.accessibility, ...(parsed.accessibility as Record<string, unknown> || {}) },
          advanced: { ...this.settings.advanced, ...(parsed.advanced as Record<string, unknown> || {}) },
          about: { ...this.settings.about, ...(parsed.about as Record<string, unknown> || {}) },
          ambient: { ...this.settings.ambient, ...(parsed.ambient as Record<string, unknown> || {}) },
          captions: { ...this.settings.captions, ...(parsed.captions as Record<string, unknown> || {}) }
        };
      }
    } catch (e) {
      log.warn('[SettingsProcess] Failed to load settings', e instanceof Error ? { error: e.message, stack: e.stack } : { error: String(e) });
    }
  }

  private saveSettings(): void {
    try {
      localStorage.setItem('pivot-settings', JSON.stringify(this.settings));
      bus.emit('settings.updated' as any, { settings: this.settings } as any);
    } catch (e) {
      log.error('[SettingsProcess] Failed to save settings', e instanceof Error ? { error: e.message, stack: e.stack } : { error: String(e) });
    }
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[SettingsProcess] onStart called for process ${this.id}`);
    
    // Auto-show settings view when process starts
    this.showSettings(ctx);
    
    // Listen for settings.show event (for re-showing if dismissed)
    bus.on('settings.show', () => {
      this.showSettings(ctx);
    });
    
    // Check URL for /settings route
    if (window.location.pathname.includes('/settings')) {
      this.showSettings(ctx);
    }
    
    // Sync initial user state if available
    if (state.user) {
      this.syncUserProfile(state.user);
    }

    // Listen for user state changes to sync profile
    bus.on('state.user', (user: any) => {
      this.syncUserProfile(user);
    });
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private syncUserProfile(user: any): void {
    if (!user) return;
    
    let changed = false;
    if (user.email && user.email !== this.settings.profile.email) {
      this.settings.profile.email = user.email;
      changed = true;
    }
    if (user.displayName && user.displayName !== this.settings.profile.displayName) {
      this.settings.profile.displayName = user.displayName;
      changed = true;
    }
    
    if (changed) {
      this.updateSettings(this.settings);
    }
  }

  private showSettings(ctx: ProcessContext): void {
    // If view already exists, just ensure it's registered (in case it was dismissed)
    if (this.settingsView) {
      const viewInstance = this.settingsView.toViewInstance();
      if (!this.views.has(viewInstance.id)) {
        ctx.registerView(viewInstance);
        this.views.set(viewInstance.id, viewInstance);
      }
      return;
    }
    
    // Create new view
    const view = new SettingsView(
      this.id,
      `${this.id}-main`,
      this.settings,
      this
    );
    const viewInstance = view.toViewInstance();
    this.settingsView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  updateSettings(updates: Partial<SettingsData>): void {
    this.settings = { ...this.settings, ...updates };
    this.saveSettings();
    if (this.settingsView) {
      this.settingsView.updateSettings(this.settings);
    }
  }

  getSettings(): SettingsData {
    return this.settings;
  }
}

/**
 * Settings View
 * 
 * Production-ready tabbed settings interface.
 */
class SettingsView extends BaseViewHTML {
  private selectedTab: SettingsTab = 'profile';
  private settings: SettingsData;
  private scrollPositions: Map<SettingsTab, number> = new Map();
  private isRenderingAmbientStems = false;

  constructor(
    processId: string,
    viewId: string,
    settings: SettingsData,
    private process: SettingsProcess
  ) {
    super(processId, viewId, {
      acceptableSizes: [
        { cols: 6, rows: 8 },
        { cols: 5, rows: 10 },
        { cols: 7, rows: 7 }
      ],
      showDismissButton: true,
      resizable: true,
      sizes: [1, 3] // [1] = full+standard (default), [3] = full+double
    });
    this.settings = settings;
  }

  protected getContentHTML(): string {
    const tabs = this.renderTabs();
    const content = this.renderTabContent();
    
    return createContainer(
      createHeading(2, 'Settings', { margin: `0 0 ${designTokens.padding.lg} 0` }) +
      `
        <!-- Tabs -->
        <div style="
          display: flex;
          gap: ${designTokens.padding.xs};
          margin-bottom: ${designTokens.padding.lg};
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: ${designTokens.padding.xs};
          border-bottom: 1px solid ${designTokens.borderColor};
        ">
          ${tabs}
        </div>
        
        <!-- Tab Content -->
        <div class="settings-tab-content" style="
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: ${designTokens.padding.sm};
        ">
          ${content}
        </div>
      `,
      { flexDirection: 'column', padding: 'lg' }
    );
  }

  private renderTabs(): string {
    const tabConfig: Array<{ id: SettingsTab; label: string; icon?: string }> = [
      { id: 'profile', label: 'Profile' },
      { id: 'preferences', label: 'Preferences' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'privacy', label: 'Privacy & Data' },
      { id: 'voice', label: 'Voice & Audio' },
      { id: 'captions', label: 'Captions' },
      { id: 'ambient', label: 'Ambient' },
      { id: 'ai', label: 'AI Behavior' },
      { id: 'storage', label: 'Storage & Sync' },
      { id: 'billing', label: 'Billing' },
      { id: 'security', label: 'Security' },
      { id: 'accessibility', label: 'Accessibility' },
      { id: 'advanced', label: 'Advanced' },
      { id: 'about', label: 'About' }
    ];

    return tabConfig.map(tab => `
      <button
        class="settings-tab"
        data-tab="${tab.id}"
        style="
          padding: ${designTokens.padding.sm} ${designTokens.padding.md};
          border: none;
          border-bottom: 2px solid ${this.selectedTab === tab.id ? designTokens.accent : 'transparent'};
          background: ${this.selectedTab === tab.id ? 'rgba(102, 204, 255, 0.1)' : 'transparent'};
          color: ${this.selectedTab === tab.id ? designTokens.accent : designTokens.contentColorMuted};
          cursor: pointer;
          border-radius: ${designTokens.radius.sm} ${designTokens.radius.sm} 0 0;
          font-size: ${designTokens.fontSize.sm};
          white-space: nowrap;
          transition: all 0.2s;
        "
        onmouseover="this.style.background='rgba(102, 204, 255, 0.05)'"
        onmouseout="this.style.background='${this.selectedTab === tab.id ? 'rgba(102, 204, 255, 0.1)' : 'transparent'}'"
      >
        ${this.escapeHtml(tab.label)}
      </button>
    `).join('');
  }

  private renderTabContent(): string {
    switch (this.selectedTab) {
      case 'profile':
        return this.renderProfileTab();
      case 'preferences':
        return this.renderPreferencesTab();
      case 'notifications':
        return this.renderNotificationsTab();
      case 'privacy':
        return this.renderPrivacyTab();
      case 'voice':
        return this.renderVoiceTab();
      case 'captions':
        return this.renderCaptionsTab();
      case 'ambient':
        return this.renderAmbientTab();
      case 'ai':
        return this.renderAITab();
      case 'storage':
        return this.renderStorageTab();
      case 'billing':
        return this.renderBillingTab();
      case 'security':
        return this.renderSecurityTab();
      case 'accessibility':
        return this.renderAccessibilityTab();
      case 'advanced':
        return this.renderAdvancedTab();
      case 'about':
        return this.renderAboutTab();
      default:
        return '<p>Unknown tab</p>';
    }
  }

  private renderProfileTab(): string {
    const { profile } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Account Information', `
          ${createFormField('Display Name', 
            this.insertDataAttribute(
              createTextInput({
                value: profile.displayName,
                className: 'settings-input',
                type: 'text'
              }),
              'data-setting',
              'profile.displayName'
            )
          )}
          ${createFormField('Email',
            this.insertDataAttribute(
              createTextInput({
                value: profile.email,
                className: 'settings-input',
                type: 'email'
              }),
              'data-setting',
              'profile.email'
            )
          )}
          ${createFormField('Bio',
            this.insertDataAttribute(
              createTextInput({
                value: profile.bio || '',
                className: 'settings-input',
                type: 'textarea',
                rows: 3
              }),
              'data-setting',
              'profile.bio'
            )
          )}
        `)}
      </div>
    `;
  }

  private renderPreferencesTab(): string {
    const { preferences } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Appearance', `
          ${this.renderField('UI Theme', `
            <select class="settings-input" data-setting="preferences.theme" style="${this.getInputStyle()}">
              <option value="light" ${preferences.theme === 'light' ? 'selected' : ''}>Light</option>
              <option value="dark" ${preferences.theme === 'dark' ? 'selected' : ''}>Dark</option>
              <option value="auto" ${preferences.theme === 'auto' ? 'selected' : ''}>Auto</option>
            </select>
          `)}
          ${this.renderField('Environment Theme', `
            <select class="settings-input" data-setting="preferences.backgroundTheme" style="${this.getInputStyle()}">
              <option value="dark" ${preferences.backgroundTheme === 'dark' ? 'selected' : ''}>Dark (Night)</option>
              <option value="day" ${preferences.backgroundTheme === 'day' ? 'selected' : ''}>Day (Clear Sky)</option>
            </select>
          `)}
          ${this.renderField('Font Size', `
            <select class="settings-input" data-setting="accessibility.fontSize" style="${this.getInputStyle()}">
              <option value="small" ${this.settings.accessibility.fontSize === 'small' ? 'selected' : ''}>Small</option>
              <option value="medium" ${this.settings.accessibility.fontSize === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="large" ${this.settings.accessibility.fontSize === 'large' ? 'selected' : ''}>Large</option>
              <option value="xlarge" ${this.settings.accessibility.fontSize === 'xlarge' ? 'selected' : ''}>Extra Large</option>
            </select>
          `)}
        `)}
        ${this.renderSection('Localization', `
          ${this.renderField('Language', `
            <select class="settings-input" data-setting="preferences.language" style="${this.getInputStyle()}">
              <option value="en" ${preferences.language === 'en' ? 'selected' : ''}>English</option>
              <option value="es" ${preferences.language === 'es' ? 'selected' : ''}>Spanish</option>
              <option value="fr" ${preferences.language === 'fr' ? 'selected' : ''}>French</option>
              <option value="de" ${preferences.language === 'de' ? 'selected' : ''}>German</option>
              <option value="ja" ${preferences.language === 'ja' ? 'selected' : ''}>Japanese</option>
              <option value="zh" ${preferences.language === 'zh' ? 'selected' : ''}>Chinese</option>
            </select>
          `)}
          ${this.renderField('Timezone', `
            <select class="settings-input" data-setting="preferences.timezone" style="${this.getInputStyle()}">
              <option value="${preferences.timezone}" selected>${preferences.timezone}</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Chicago">America/Chicago (CST)</option>
              <option value="America/Denver">America/Denver (MST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Europe/Paris">Europe/Paris (CET)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
              <option value="UTC">UTC</option>
            </select>
          `)}
          ${this.renderField('Date Format', `
            <select class="settings-input" data-setting="preferences.dateFormat" style="${this.getInputStyle()}">
              <option value="MM/DD/YYYY" ${preferences.dateFormat === 'MM/DD/YYYY' ? 'selected' : ''}>MM/DD/YYYY</option>
              <option value="DD/MM/YYYY" ${preferences.dateFormat === 'DD/MM/YYYY' ? 'selected' : ''}>DD/MM/YYYY</option>
              <option value="YYYY-MM-DD" ${preferences.dateFormat === 'YYYY-MM-DD' ? 'selected' : ''}>YYYY-MM-DD</option>
            </select>
          `)}
          ${this.renderField('Time Format', `
            <select class="settings-input" data-setting="preferences.timeFormat" style="${this.getInputStyle()}">
              <option value="12h" ${preferences.timeFormat === '12h' ? 'selected' : ''}>12-hour</option>
              <option value="24h" ${preferences.timeFormat === '24h' ? 'selected' : ''}>24-hour</option>
            </select>
          `)}
          ${this.renderField('First Day of Week', `
            <select class="settings-input" data-setting="preferences.firstDayOfWeek" style="${this.getInputStyle()}">
              <option value="sunday" ${preferences.firstDayOfWeek === 'sunday' ? 'selected' : ''}>Sunday</option>
              <option value="monday" ${preferences.firstDayOfWeek === 'monday' ? 'selected' : ''}>Monday</option>
            </select>
          `)}
        `)}
      </div>
    `;
  }

  private renderNotificationsTab(): string {
    const { notifications } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Notification Channels', `
          ${this.renderToggle('Email Notifications', 'notifications.emailEnabled', notifications.emailEnabled)}
          ${this.renderToggle('Push Notifications', 'notifications.pushEnabled', notifications.pushEnabled)}
          ${this.renderToggle('Desktop Notifications', 'notifications.desktopNotifications', notifications.desktopNotifications)}
          ${this.renderToggle('Sound Effects', 'notifications.soundEnabled', notifications.soundEnabled)}
        `)}
        ${this.renderSection('Frequency', `
          ${this.renderField('Proactive Message Frequency', `
            <select class="settings-input" data-setting="notifications.proactiveFrequency" style="${this.getInputStyle()}">
              <option value="never" ${notifications.proactiveFrequency === 'never' ? 'selected' : ''}>Never</option>
              <option value="low" ${notifications.proactiveFrequency === 'low' ? 'selected' : ''}>Low</option>
              <option value="normal" ${notifications.proactiveFrequency === 'normal' ? 'selected' : ''}>Normal</option>
              <option value="high" ${notifications.proactiveFrequency === 'high' ? 'selected' : ''}>High</option>
            </select>
          `)}
          ${this.renderField('Email Digest', `
            <select class="settings-input" data-setting="notifications.emailDigest" style="${this.getInputStyle()}">
              <option value="never" ${notifications.emailDigest === 'never' ? 'selected' : ''}>Never</option>
              <option value="daily" ${notifications.emailDigest === 'daily' ? 'selected' : ''}>Daily</option>
              <option value="weekly" ${notifications.emailDigest === 'weekly' ? 'selected' : ''}>Weekly</option>
            </select>
          `)}
        `)}
      </div>
    `;
  }

  private renderPrivacyTab(): string {
    const { privacy } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Data Sharing', `
          ${this.renderToggle('Data Sharing', 'privacy.dataSharing', privacy.dataSharing)}
          ${this.renderToggle('Analytics Opt-Out', 'privacy.analyticsOptOut', privacy.analyticsOptOut)}
          ${this.renderToggle('Crash Reporting', 'privacy.crashReporting', privacy.crashReporting)}
          ${this.renderToggle('Usage Data', 'privacy.usageData', privacy.usageData)}
        `)}
        ${this.renderSection('Data Management', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.md};">
            ${this.insertDataAttribute(
              createButton('Export My Data', {
                className: 'settings-button',
                variant: 'secondary'
              }),
              'data-action',
              'export-data'
            )}
            ${this.insertDataAttribute(
              createButton('Delete Account', {
                className: 'settings-button',
                variant: 'secondary',
                style: `
                  background: rgba(255, 59, 48, 0.2);
                  border-color: rgba(255, 59, 48, 0.4);
                  color: #ff3b30;
                `
              }),
              'data-action',
              'delete-account'
            )}
          </div>
        `)}
      </div>
    `;
  }

  private renderVoiceTab(): string {
    const { voice } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Microphone', `
          ${this.renderToggle('Microphone Enabled', 'voice.microphoneEnabled', voice.microphoneEnabled)}
          ${this.renderField('Wake Word', `
            <input
              type="text"
              value="${this.escapeHtml(voice.wakeWord)}"
              class="settings-input"
              data-setting="voice.wakeWord"
              style="${this.getInputStyle()}"
            />
          `)}
        `)}
        ${this.renderSection('Text-to-Speech', `
          ${this.renderToggle('TTS Enabled', 'voice.ttsEnabled', voice.ttsEnabled)}
          ${this.renderField('TTS Voice', `
            <select class="settings-input" data-setting="voice.ttsVoice" style="${this.getInputStyle()}">
              <option value="default" ${voice.ttsVoice === 'default' ? 'selected' : ''}>Default</option>
              <option value="male" ${voice.ttsVoice === 'male' ? 'selected' : ''}>Male</option>
              <option value="female" ${voice.ttsVoice === 'female' ? 'selected' : ''}>Female</option>
            </select>
          `)}
          ${this.renderField('TTS Speed', `
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value="${voice.ttsSpeed}"
              class="settings-input"
              data-setting="voice.ttsSpeed"
              style="width: 100%;"
            />
            <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.xs};">
              ${voice.ttsSpeed}x
            </span>
          `)}
        `)}
        ${this.renderSection('Audio Output', `
          ${this.renderField('Audio Device', `
            <select class="settings-input" data-setting="voice.audioOutput" style="${this.getInputStyle()}">
              <option value="default" ${voice.audioOutput === 'default' ? 'selected' : ''}>Default</option>
            </select>
          `)}
        `)}
      </div>
    `;
  }

  private renderCaptionsTab(): string {
    const { captions } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Caption Settings', `
          ${this.renderToggle('Enable Captions', 'captions.enabled', captions?.enabled ?? true)}
          ${this.renderField('Caption Size', `
            <select class="settings-input" data-setting="captions.size" style="${this.getInputStyle()}">
              <option value="small" ${captions?.size === 'small' ? 'selected' : ''}>Small</option>
              <option value="medium" ${captions?.size === 'medium' ? 'selected' : ''}>Medium (Default)</option>
              <option value="large" ${captions?.size === 'large' ? 'selected' : ''}>Large</option>
              <option value="xlarge" ${captions?.size === 'xlarge' ? 'selected' : ''}>Extra Large</option>
            </select>
          `)}
          <p style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            margin: ${designTokens.padding.md} 0 0 0;
          ">
            Captions appear at the bottom of the screen during speech playback.
            They use an RSVP (Rapid Serial Visual Presentation) format for faster reading.
          </p>
        `)}
      </div>
    `;
  }

  private renderAmbientTab(): string {
    const { ambient } = this.settings;
    const currentSuite = ambient.suite || 'final';
    const ambientEnabled = ambient.enabled ?? true;

    const suites = [
      { value: 'final', label: 'Final', description: 'Pad + Rain + Spark + Piano' },
      { value: 'dawn', label: 'Dawn', description: 'Brighter pad, wind, glass sparkles, suspended piano' },
      { value: 'day', label: 'Day', description: 'Sky pad, natural airflow, chimes, piano' },
      { value: 'dream', label: 'Dream', description: 'Dream pad, haze, particles, piano' },
      { value: 'womb', label: 'Womb', description: 'Fluid tone, heartbeat, breath/haze' }
    ];

    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Ambient Suite', `
          ${this.renderToggle('Enable Ambient Audio', 'ambient.enabled', ambientEnabled)}
          ${this.renderField('Select Ambient Theme', `
            <select class="settings-input" data-setting="ambient.suite" style="${this.getInputStyle()}">
              ${suites.map(suite => `
                <option value="${suite.value}" ${currentSuite === suite.value ? 'selected' : ''}>
                  ${suite.label} - ${suite.description}
                </option>
              `).join('')}
            </select>
          `)}
          <p style="
            color: ${designTokens.contentColorMuted};
            font-size: ${designTokens.fontSize.sm};
            margin: ${designTokens.padding.md} 0 0 0;
          ">
            Choose your ambient soundscape. The selection is saved automatically and will persist across sessions.
          </p>
        `)}
      </div>
    `;
  }


  private renderAITab(): string {
    const { ai } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Response Style', `
          ${this.renderField('Response Style', `
            <select class="settings-input" data-setting="ai.responseStyle" style="${this.getInputStyle()}">
              <option value="concise" ${ai.responseStyle === 'concise' ? 'selected' : ''}>Concise</option>
              <option value="balanced" ${ai.responseStyle === 'balanced' ? 'selected' : ''}>Balanced</option>
              <option value="detailed" ${ai.responseStyle === 'detailed' ? 'selected' : ''}>Detailed</option>
            </select>
          `)}
          ${this.renderField('Proactivity', `
            <select class="settings-input" data-setting="ai.proactivity" style="${this.getInputStyle()}">
              <option value="passive" ${ai.proactivity === 'passive' ? 'selected' : ''}>Passive</option>
              <option value="moderate" ${ai.proactivity === 'moderate' ? 'selected' : ''}>Moderate</option>
              <option value="proactive" ${ai.proactivity === 'proactive' ? 'selected' : ''}>Proactive</option>
            </select>
          `)}
          ${this.renderField('Context Length', `
            <select class="settings-input" data-setting="ai.contextLength" style="${this.getInputStyle()}">
              <option value="short" ${ai.contextLength === 'short' ? 'selected' : ''}>Short</option>
              <option value="medium" ${ai.contextLength === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="long" ${ai.contextLength === 'long' ? 'selected' : ''}>Long</option>
            </select>
          `)}
          ${this.renderField('Temperature', `
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value="${ai.temperature}"
              class="settings-input"
              data-setting="ai.temperature"
              style="width: 100%;"
            />
            <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.xs};">
              ${ai.temperature}
            </span>
          `)}
        `)}
        ${this.renderSection('Personality', `
          ${this.renderField('System Personality', `
            <textarea
              class="settings-input"
              data-setting="ai.systemPersonality"
              style="${this.getTextareaStyle()}"
              rows="3"
            >${this.escapeHtml(ai.systemPersonality)}</textarea>
          `)}
        `)}
      </div>
    `;
  }

  private renderStorageTab(): string {
    const { storage } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Sync & Backup', `
          ${this.renderToggle('Cloud Sync', 'storage.cloudSyncEnabled', storage.cloudSyncEnabled)}
          ${this.renderToggle('Auto Backup', 'storage.autoBackup', storage.autoBackup)}
          ${this.renderField('Backup Frequency', `
            <select class="settings-input" data-setting="storage.backupFrequency" style="${this.getInputStyle()}">
              <option value="daily" ${storage.backupFrequency === 'daily' ? 'selected' : ''}>Daily</option>
              <option value="weekly" ${storage.backupFrequency === 'weekly' ? 'selected' : ''}>Weekly</option>
              <option value="monthly" ${storage.backupFrequency === 'monthly' ? 'selected' : ''}>Monthly</option>
            </select>
          `)}
        `)}
        ${this.renderSection('Storage Usage', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">
            <div style="display: flex; justify-content: space-between; color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">
              <span>Cloud Storage Used</span>
              <span>${storage.cloudStorageUsed} MB / Unlimited</span>
            </div>
            <div style="display: flex; justify-content: space-between; color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">
              <span>Local Storage Limit</span>
              <span>${storage.localStorageLimit} MB</span>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private renderBillingTab(): string {
    const { billing } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Subscription', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.md};">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${designTokens.contentColor}; font-weight: 500;">Plan</span>
              <span style="color: ${designTokens.accent}; text-transform: capitalize;">${billing.plan}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">Status</span>
              <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm}; text-transform: capitalize;">${billing.subscriptionStatus}</span>
            </div>
            ${billing.nextBillingDate ? `
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">Next Billing Date</span>
                <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">${billing.nextBillingDate}</span>
              </div>
            ` : ''}
            ${this.insertDataAttribute(
              createButton('Manage Subscription', {
                className: 'settings-button',
                variant: 'primary'
              }),
              'data-action',
              'manage-subscription'
            )}
          </div>
        `)}
        ${this.renderSection('Usage This Month', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">
            <div style="display: flex; justify-content: space-between; color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">
              <span>Requests</span>
              <span>${billing.usageThisMonth.requests.toLocaleString()}</span>
            </div>
            <div style="display: flex; justify-content: space-between; color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">
              <span>Storage</span>
              <span>${billing.usageThisMonth.storage} MB</span>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private renderSecurityTab(): string {
    const { security } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Authentication', `
          ${this.renderToggle('Two-Factor Authentication', 'security.twoFactorEnabled', security.twoFactorEnabled)}
          ${this.renderToggle('Login Alerts', 'security.loginAlerts', security.loginAlerts)}
          ${this.renderToggle('Require Password for Sensitive Actions', 'security.requirePasswordForSensitive', security.requirePasswordForSensitive)}
          ${this.renderField('Session Timeout', `
            <select class="settings-input" data-setting="security.sessionTimeout" style="${this.getInputStyle()}">
              <option value="15" ${security.sessionTimeout === 15 ? 'selected' : ''}>15 minutes</option>
              <option value="30" ${security.sessionTimeout === 30 ? 'selected' : ''}>30 minutes</option>
              <option value="60" ${security.sessionTimeout === 60 ? 'selected' : ''}>1 hour</option>
              <option value="120" ${security.sessionTimeout === 120 ? 'selected' : ''}>2 hours</option>
              <option value="0" ${security.sessionTimeout === 0 ? 'selected' : ''}>Never</option>
            </select>
          `)}
        `)}
        ${this.renderSection('Active Sessions', `
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">Active Sessions</span>
            <span style="color: ${designTokens.contentColor}; font-size: ${designTokens.fontSize.sm};">${security.activeSessions}</span>
          </div>
          ${this.insertDataAttribute(
            createButton('Manage Sessions', {
              className: 'settings-button',
              variant: 'secondary'
            }),
            'data-action',
            'manage-sessions'
          )}
        `)}
      </div>
    `;
  }

  private renderAccessibilityTab(): string {
    const { accessibility } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Display', `
          ${this.renderToggle('Screen Reader Support', 'accessibility.screenReader', accessibility.screenReader)}
          ${this.renderToggle('High Contrast Mode', 'accessibility.highContrast', accessibility.highContrast)}
          ${this.renderToggle('Reduced Motion', 'accessibility.reducedMotion', accessibility.reducedMotion)}
        `)}
        ${this.renderSection('Interaction', `
          ${this.renderToggle('Keyboard Shortcuts', 'accessibility.keyboardShortcuts', accessibility.keyboardShortcuts)}
        `)}
      </div>
    `;
  }

  private renderAdvancedTab(): string {
    const { advanced } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Developer Options', `
          ${this.renderToggle('Developer Mode', 'advanced.developerMode', advanced.developerMode)}
          ${this.renderToggle('Debug Mode', 'advanced.debugMode', advanced.debugMode)}
          ${this.renderToggle('Experimental Features', 'advanced.experimentalFeatures', advanced.experimentalFeatures)}
          ${this.renderField('Log Level', `
            <select class="settings-input" data-setting="advanced.logLevel" style="${this.getInputStyle()}">
              <option value="error" ${advanced.logLevel === 'error' ? 'selected' : ''}>Error</option>
              <option value="warn" ${advanced.logLevel === 'warn' ? 'selected' : ''}>Warn</option>
              <option value="info" ${advanced.logLevel === 'info' ? 'selected' : ''}>Info</option>
              <option value="debug" ${advanced.logLevel === 'debug' ? 'selected' : ''}>Debug</option>
            </select>
          `)}
        `)}
        ${this.renderSection('API', `
          ${this.renderField('API Key', `
            <div style="display: flex; gap: ${designTokens.padding.sm};">
              ${this.insertDataAttribute(
                createTextInput({
                  value: advanced.apiKey || '',
                  className: 'settings-input',
                  type: 'password',
                  placeholder: 'Not set',
                  style: 'flex: 1;'
                }),
                'data-setting',
                'advanced.apiKey'
              )}
              ${this.insertDataAttribute(
                createButton('Generate', {
                  className: 'settings-button',
                  variant: 'secondary'
                }),
                'data-action',
                'generate-api-key'
              )}
            </div>
          `)}
        `)}
      </div>
    `;
  }

  private renderAboutTab(): string {
    const { about } = this.settings;
    return `
      <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.xl};">
        ${this.renderSection('Application', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.md};">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">Version</span>
              <span style="color: ${designTokens.contentColor}; font-size: ${designTokens.fontSize.sm};">${about.version}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">Build Date</span>
              <span style="color: ${designTokens.contentColor}; font-size: ${designTokens.fontSize.sm};">${about.buildDate}</span>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${designTokens.contentColorMuted}; font-size: ${designTokens.fontSize.sm};">License</span>
              <span style="color: ${designTokens.contentColor}; font-size: ${designTokens.fontSize.sm};">${about.license}</span>
            </div>
          </div>
        `)}
        ${this.renderSection('Links', `
          <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.sm};">
            <a href="#" style="color: ${designTokens.accent}; text-decoration: none; font-size: ${designTokens.fontSize.sm};" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">Documentation</a>
            <a href="#" style="color: ${designTokens.accent}; text-decoration: none; font-size: ${designTokens.fontSize.sm};" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">Privacy Policy</a>
            <a href="#" style="color: ${designTokens.accent}; text-decoration: none; font-size: ${designTokens.fontSize.sm};" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">Terms of Service</a>
            <a href="#" style="color: ${designTokens.accent}; text-decoration: none; font-size: ${designTokens.fontSize.sm};" onmouseover="this.style.textDecoration='underline'" onmouseout="this.style.textDecoration='none'">Support</a>
          </div>
        `)}
      </div>
    `;
  }

  // Helper method to insert data attributes into HTML strings
  private insertDataAttribute(html: string, attribute: string, value: string): string {
    // Escape the value to prevent XSS
    const escapedValue = this.escapeHtml(value);
    
    // For self-closing tags like <input />, insert before the />
    if (html.includes(' />')) {
      return html.replace(' />', ` ${attribute}="${escapedValue}" />`);
    }
    
    // For opening tags, find the > that closes the opening tag
    // This is tricky because > might appear in attribute values (like onclick handlers)
    // Strategy: Find the > that's not inside quotes by tracking quote state
    let inQuotes = false;
    let quoteChar = '';
    let tagEndIndex = -1;
    
    for (let i = 0; i < html.length; i++) {
      const char = html[i];
      
      if ((char === '"' || char === "'") && (i === 0 || html[i - 1] !== '\\')) {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
          quoteChar = '';
        }
      } else if (char === '>' && !inQuotes) {
        tagEndIndex = i;
        break;
      }
    }
    
    if (tagEndIndex !== -1) {
      // Insert the attribute before the closing >
      return html.substring(0, tagEndIndex) + ` ${attribute}="${escapedValue}"` + html.substring(tagEndIndex);
    }
    
    return html;
  }

  // Helper methods for rendering UI components
  private renderSection(title: string, content: string): string {
    return `
      <div style="
        display: flex;
        flex-direction: column;
        gap: ${designTokens.padding.md};
        padding: ${designTokens.padding.lg};
        background: ${designTokens.cardBackground};
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.md};
      ">
        <h3 style="
          margin: 0;
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">${this.escapeHtml(title)}</h3>
        <div style="display: flex; flex-direction: column; gap: ${designTokens.padding.lg};">
          ${content}
        </div>
      </div>
    `;
  }

  private renderField(label: string, input: string): string {
    return createFormField(label, input);
  }

  private renderToggle(label: string, setting: string, value: boolean): string {
    return `
      <div style="
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: ${designTokens.padding.md} 0;
      ">
        <label style="
          font-size: ${designTokens.fontSize.sm};
          color: ${designTokens.contentColor};
          font-weight: 500;
        ">${this.escapeHtml(label)}</label>
        <label style="
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          cursor: pointer;
        ">
          <input
            type="checkbox"
            class="settings-toggle"
            data-setting="${setting}"
            ${value ? 'checked' : ''}
            style="opacity: 0; width: 0; height: 0;"
          />
          <span style="
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: ${value ? designTokens.accent : 'rgba(255, 255, 255, 0.2)'};
            transition: 0.3s;
            border-radius: 24px;
          "></span>
          <span style="
            position: absolute;
            content: '';
            height: 18px;
            width: 18px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: 0.3s;
            border-radius: 50%;
            transform: translateX(${value ? '20px' : '0'});
          "></span>
        </label>
      </div>
    `;
  }

  private getInputStyle(): string {
    return `
      padding: ${designTokens.padding.md};
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid ${designTokens.borderColor};
      border-radius: ${designTokens.radius.sm};
      color: ${designTokens.contentColor};
      font-size: ${designTokens.fontSize.sm};
      font-family: inherit;
      outline: none;
      transition: all 0.2s;
    `;
  }

  private getTextareaStyle(): string {
    return this.getInputStyle() + `
      resize: vertical;
      min-height: 60px;
    `;
  }


  protected escapeHtml(text: string): string {
    return escapeHtmlUtil(text);
  }

  protected render(): void {
    super.render();
    
    // Tab switching with scroll position preservation
    const tabButtons = this.element?.querySelectorAll('.settings-tab');
    const scrollableContent = this.element?.querySelector('.settings-tab-content') as HTMLElement;
    
    tabButtons?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = (e.target as HTMLElement).getAttribute('data-tab') as SettingsTab;
        if (tab && tab !== this.selectedTab) {
          // Save current tab's scroll position
          if (scrollableContent) {
            this.scrollPositions.set(this.selectedTab, scrollableContent.scrollTop);
          }
          
          // Switch to new tab
          this.selectedTab = tab;
          this.markDirty();
          
          // Restore new tab's scroll position after render
          // Use double requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const newScrollableContent = this.element?.querySelector('.settings-tab-content') as HTMLElement;
              if (newScrollableContent) {
                const savedPosition = this.scrollPositions.get(tab) || 0;
                newScrollableContent.scrollTop = savedPosition;
              }
            });
          });
        }
      });
    });
    
    // Restore scroll position on initial render
    if (scrollableContent) {
      const savedPosition = this.scrollPositions.get(this.selectedTab) || 0;
      scrollableContent.scrollTop = savedPosition;
    }

    // Input handlers
    const inputs = this.element?.querySelectorAll('.settings-input');
    inputs?.forEach(input => {
      const setting = (input as HTMLElement).getAttribute('data-setting');
      if (!setting) return;

      // For range inputs, update the displayed value on input (live update)
      if (input instanceof HTMLInputElement && input.type === 'range') {
        input.addEventListener('input', (e) => {
          const target = e.target as HTMLInputElement;
          const value = parseFloat(target.value);
          
          // Update the displayed value span
          const span = target.parentElement?.querySelector('span');
          if (span && setting) {
            if (setting.includes('Volume') || setting.includes('volume')) {
              span.textContent = `${value} dB`;
            } else {
              span.textContent = String(value);
            }
          }
          
          // Apply setting immediately for audio controls (for range inputs)
          if (setting.startsWith('audio.') && input instanceof HTMLInputElement && input.type === 'range') {
            this.updateSetting(setting, value);
          }
          
        });
      }

      input.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        const value = target.type === 'checkbox' 
          ? (target as HTMLInputElement).checked 
          : target.type === 'range'
          ? parseFloat(target.value)
          : target.value;
        
        this.updateSetting(setting, value);
      });
    });

      // Toggle handlers
      const toggles = this.element?.querySelectorAll('.settings-toggle');
      toggles?.forEach(toggle => {
        const setting = (toggle as HTMLElement).getAttribute('data-setting');
        if (!setting) return;

        toggle.addEventListener('change', (e) => {
          const checked = (e.target as HTMLInputElement).checked;
          // Handle inverted toggle for master.muted
          if (setting === 'audio.master.muted') {
            // Toggle shows: true = enabled (not muted), false = disabled (muted)
            // We store: muted = true means muted, muted = false means not muted
            // So we need to invert: if toggle is true (enabled), muted should be false
            this.updateSetting(setting, !checked);
          } else {
            this.updateSetting(setting, checked);
          }
        });
      });

    // Button handlers
    const buttons = this.element?.querySelectorAll('.settings-button');
    buttons?.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = (e.target as HTMLElement).getAttribute('data-action');
        if (action) {
          this.handleAction(action);
        }
      });
    });

    // Handle ambient suite changes (event is emitted in updateSetting)
    const ambientSelect = this.element?.querySelector('[data-setting="ambient.suite"]') as HTMLSelectElement;
    ambientSelect?.addEventListener('change', (e) => {
      const suite = (e.target as HTMLSelectElement).value;
      this.updateSetting('ambient.suite', suite);
    });
  }

  private updateSetting(path: string, value: unknown): void {
    // Track previous language value for event emission
    const previousLanguage = path === 'preferences.language' ? this.settings.preferences.language : null;
    
    const keys = path.split('.');
    let obj: any = this.settings;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!(keys[i] in obj)) {
        obj[keys[i]] = {};
      }
      obj = obj[keys[i]];
    }
    
    obj[keys[keys.length - 1]] = value;
    
    // Save to localStorage immediately (deep merge to preserve nested structure)
    this.process.updateSettings(this.settings);
    this.markDirty();
    
    // Emit background theme change event
    if (path === 'preferences.backgroundTheme') {
      // Dynamic import to avoid circular dependency
      import('../../services/majaku-privacy-background/index.js').then(({ setTheme }) => {
        setTheme(value as 'dark' | 'day');
      });
    }
    
    // Emit language change event if language was changed
    if (path === 'preferences.language' && previousLanguage && previousLanguage !== value) {
      bus.emit('settings.language.changed', {
        locale: value as string,
        previousLocale: previousLanguage,
        timestamp: Date.now()
      });
    }
    
    // Emit ambient suite change event if ambient suite was changed
    if (path === 'ambient.suite') {
      bus.emit('settings.ambient.suite.changed', {
        suite: value as 'final' | 'dawn' | 'day' | 'dream' | 'womb',
        timestamp: Date.now()
      });
    } else if (path === 'ambient.enabled') {
      bus.emit('settings.ambient.enabled.changed', {
        enabled: Boolean(value),
        timestamp: Date.now()
      });
    } else if (path.startsWith('captions.')) {
      bus.emit('settings.captions.changed', {
        settings: this.settings.captions,
        timestamp: Date.now()
      });
    }
  }


  private async handleAction(action: string): Promise<void> {
    switch (action) {
      case 'export-data':
        bus.emit('settings.export-data' as any, {} as any);
        alert('Data export initiated. You will receive an email when ready.');
        break;
      case 'delete-account':
        if (confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
          bus.emit('settings.delete-account' as any, {} as any);
          alert('Account deletion requested. You will receive a confirmation email.');
        }
        break;
      case 'manage-subscription':
        bus.emit('settings.manage-subscription' as any, {} as any);
        window.open('/billing', '_blank');
        break;
      case 'manage-sessions':
        bus.emit('settings.manage-sessions' as any, {} as any);
        alert('Session management coming soon.');
        break;
      case 'generate-api-key':
        const newKey = 'pk_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        this.updateSetting('advanced.apiKey', newKey);
        alert('New API key generated. Please save it securely.');
        break;
      default:
        log.warn(`[SettingsView] Unknown action: ${action}`);
    }
  }

  updateSettings(newSettings: SettingsData): void {
    this.settings = newSettings;
    this.markDirty();
  }
}

registerProcess('settings', (id: string) => new SettingsProcess(id));
